/* eslint-disable */
/**
 * Phase 98m — Recherche par lot pour rappel produit HACCP.
 *
 * "Le lot L12345 est contaminé — où sont parties ses barquettes ?"
 * → liste chaque destination (revendeur, distributeur, dépôt) + qty + statut.
 */
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ShieldAlert, Search, ArrowLeft, MapPin, Truck, Package,
  AlertTriangle, CheckCircle2,
} from "lucide-react";

export default function HaccpLotTrace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lotNumber, setLotNumber] = useState(searchParams.get("lot") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runTrace = async (query) => {
    const q = (query ?? lotNumber).trim();
    if (!q) {
      setError("Saisissez un numéro de lot");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/haccp/lot-trace?lot_number=${encodeURIComponent(q)}`);
      setResult(data);
      setSearchParams({ lot: q });
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur recherche");
    } finally {
      setLoading(false);
    }
  };

  // Lance auto si ?lot= en URL au 1er load
  React.useEffect(() => {
    const initialLot = searchParams.get("lot");
    if (initialLot && !result && !loading) {
      runTrace(initialLot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto" data-testid="haccp-lot-trace-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/contacts">
          <Button variant="ghost" size="sm" data-testid="haccp-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Carnet
          </Button>
        </Link>
        <div className="w-10 h-10 rounded-md bg-[#C05C47] flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-semibold text-[#1A1C1A]">Traçabilité HACCP — Recherche par lot</h1>
          <p className="text-sm text-[#6B6E6B]">
            En cas de rappel produit : trouvez immédiatement où sont parties les barquettes d'un lot précis.
          </p>
        </div>
      </div>

      {/* Recherche */}
      <div className="bg-white border border-[#E5E5E0] rounded-md p-4 space-y-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">Numéro de lot</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6E6B]" />
              <Input
                placeholder="Ex : L20260628-001 (recherche partielle acceptée)"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runTrace()}
                className="pl-9 font-mono"
                data-testid="lot-search-input"
              />
            </div>
          </div>
          <Button
            onClick={() => runTrace()}
            disabled={loading || !lotNumber.trim()}
            className="bg-[#C05C47] hover:bg-[#A34734]"
            data-testid="lot-search-btn"
          >
            {loading ? "Recherche…" : "Tracer"}
          </Button>
        </div>
        {error && (
          <div className="text-sm text-[#C05C47] flex items-center gap-2" data-testid="lot-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
      </div>

      {/* Résultats */}
      {result && (
        <>
          {/* Résumé */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="lot-summary">
            <StatCard label="Lots trouvés" value={result.summary?.total_lots_matched || 0} color="#1F4E3D" icon={Package} />
            <StatCard label="Livraisons B2B" value={result.summary?.total_deliveries || 0} color="#C05C47" icon={Truck} />
            <StatCard label="Mouvements stock" value={result.summary?.total_movements || 0} color="#C1A063" icon={MapPin} />
            <StatCard label="En stock" value={result.summary?.quantity_in_stock || 0} color="#2E5C8A" icon={CheckCircle2} />
          </div>

          {/* Lots identifiés */}
          {result.lots_found?.length > 0 && (
            <section className="bg-white border border-[#E5E5E0] rounded-md p-4" data-testid="lots-found">
              <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#1F4E3D]" /> Lots identifiés
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {result.lots_found.map((l) => (
                  <div key={l.id} className="border border-[#E5E5E0] p-2 rounded text-xs bg-[#FAFAF7] font-mono">
                    <div className="font-semibold text-[#1A1C1A]">{l.lot_number || l.id.slice(0, 8)}</div>
                    <div className="text-[#6B6E6B]">DLC : {(l.dlc || "?").slice(0, 10)}</div>
                    {l.production_date && <div className="text-[#6B6E6B]">Fabrication : {l.production_date.slice(0, 10)}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Livraisons revendeurs */}
          <section className="bg-white border border-[#E5E5E0] rounded-md p-4" data-testid="deliveries-section">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#C05C47]" />
              Livraisons ({result.deliveries_to_resellers?.length || 0})
            </h2>
            {!result.deliveries_to_resellers?.length ? (
              <p className="text-xs text-[#6B6E6B] italic">Aucune livraison recensée avec ce lot.</p>
            ) : (
              <div className="space-y-3">
                {result.deliveries_to_resellers.map((d) => (
                  <div key={d.delivery_id} className="border-l-4 border-[#C05C47] pl-3 py-2 bg-[#FAFAF7]" data-testid={`delivery-${d.delivery_id}`}>
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <div>
                        <div className="font-mono font-semibold text-sm">{d.reference || d.delivery_id.slice(0, 8)}</div>
                        <div className="text-xs text-[#6B6E6B]">
                          {d.destination_type} · <strong>{d.destination_name}</strong>
                        </div>
                        <div className="text-[10px] text-[#6B6E6B]">
                          Livré le {(d.delivery_date || "").slice(0, 10)} · Statut {d.status}
                        </div>
                      </div>
                      <Badge className="bg-[#C05C47] text-white text-xs">
                        {d.quantity_matched} barq
                      </Badge>
                    </div>
                    <div className="mt-2 space-y-0.5">
                      {d.matching_lines.map((ln, i) => (
                        <div key={i} className="text-[11px] font-mono flex justify-between">
                          <span>{ln.product_name} · lot {ln.lot_number} · DLC {(ln.dlc || "?").slice(0, 10)}</span>
                          <span className="text-[#1F4E3D] font-semibold">{ln.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Mouvements stock */}
          <section className="bg-white border border-[#E5E5E0] rounded-md p-4" data-testid="movements-section">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C1A063]" />
              Mouvements stock ({result.movements_to_distributors?.length || 0})
            </h2>
            {!result.movements_to_distributors?.length ? (
              <p className="text-xs text-[#6B6E6B] italic">Aucun mouvement stock associé.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-1">
                {result.movements_to_distributors.map((mv) => (
                  <div key={mv.movement_id} className="text-xs font-mono border-b border-[#E5E5E0] py-1 flex justify-between gap-2" data-testid={`movement-${mv.movement_id}`}>
                    <span className="flex-1 truncate">
                      <Badge variant="outline" className="mr-1 text-[10px]">{mv.type}</Badge>
                      {mv.distributor_name} <span className="text-[#6B6E6B]">({mv.distributor_type})</span>
                    </span>
                    <span className="text-[#1F4E3D] font-semibold">{mv.quantity}</span>
                    <span className="text-[10px] text-[#6B6E6B] w-24 text-right">{(mv.created_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Stock actuel */}
          <section className="bg-white border border-[#E5E5E0] rounded-md p-4" data-testid="stock-section">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#1F4E3D]" />
              Stock actuel restant
            </h2>
            {Object.keys(result.current_stock_by_location || {}).length === 0 ? (
              <p className="text-xs text-[#6B6E6B] italic">Aucun stock restant pour ce lot.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.values(result.current_stock_by_location).map((loc, i) => (
                  <div key={i} className="border border-[#E5E5E0] p-2 text-xs" data-testid={`stock-loc-${i}`}>
                    <div className="font-semibold">{loc.distributor_name} <span className="text-[#6B6E6B]">({loc.distributor_type})</span></div>
                    <div className="text-[#1F4E3D] font-mono font-semibold">{loc.total_qty} barq</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="border border-[#E5E5E0] p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-3 h-3" style={{ color }} />}
        <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">{label}</div>
      </div>
      <div className="text-2xl font-mono font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}
